import { useEffect, useState } from "react";
import {
  analyzeListingImage,
  createListing,
  createListingWithImages,
  deleteListing,
  updateListing,
} from "../../api/listingsApi";
import { createShop, deleteShop, listMyShops, updateShop, uploadMiniShopCover, uploadMiniShopLogo } from "../../api/shopApi";
import { API_URL } from "../../config/apiConfig";
import ShopDashboardPage from "../../pages/ShopDashboardPage";
import ShopPreviewPage from "../../pages/ShopPreviewPage";
import ShopSettingsPage from "../../pages/ShopSettingsPage";
import MiniShopsPage from "../../pages/MiniShopsPage";
import { findSelectedShop, selectedShopKey } from "../../utils/shopSelection";

function ShopDashboardContainer({ auth, listings, mediaUrl, onListingDeleted, onListingUpdated, onNavigate, onOpenListing, refreshListings, view = "dashboard" }) {
  const [shop, setShop] = useState(null);
  const [shops, setShops] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [deletingListingId, setDeletingListingId] = useState("");
  const accessToken = auth?.access_token;
  const selectedShopStorageKey = selectedShopKey(auth?.user?.id);

  useEffect(() => {
    if (!accessToken) return;
    setIsLoading(true);
    setError("");
    listMyShops({ accessToken, apiUrl: API_URL })
      .then((values) => {
        setShops(values);
        if (view !== "dashboard" && values.length) {
          const savedId = selectedShopStorageKey ? window.localStorage.getItem(selectedShopStorageKey) : "";
          setShop((current) => current || findSelectedShop(values, savedId));
        }
      })
      .catch((requestError) => setError(errorMessage(requestError)))
      .finally(() => setIsLoading(false));
  }, [accessToken, selectedShopStorageKey, view]);

  function selectShop(value) {
    setShop(value);
    if (selectedShopStorageKey) window.localStorage.setItem(selectedShopStorageKey, value.id);
  }

  function editShop(value) {
    selectShop(value);
    onNavigate("shop-settings");
  }

  async function createMiniShop(settings) {
    setIsSaving(true);
    setError("");
    try {
      const created = await createShop({ accessToken, apiUrl: API_URL, shop: settings });
      setShops((current) => [...current, created]);
      selectShop(created);
      return true;
    } catch (requestError) {
      setError(errorMessage(requestError));
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function removeShop() {
    if (!shop || !window.confirm(`Vil du slette "${shop.shop_name}"? Aktive varer i shoppen arkiveres.`)) return;
    setIsSaving(true);
    setError("");
    try {
      await deleteShop({ accessToken, apiUrl: API_URL, shopId: shop.id });
      setShops((current) => current.filter((value) => value.id !== shop.id));
      setShop(null);
      if (selectedShopStorageKey) window.localStorage.removeItem(selectedShopStorageKey);
      await refreshListings();
      onNavigate("shop");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  async function saveBranding(settings, logo, cover) {
    setIsSaving(true);
    setError("");
    try {
      let updated = await updateShop({ accessToken, apiUrl: API_URL, shop: settings, shopId: shop.id });
      if (logo) updated = await uploadMiniShopLogo({ accessToken, apiUrl: API_URL, logo, shopId: shop.id });
      if (cover) updated = await uploadMiniShopCover({ accessToken, apiUrl: API_URL, cover, shopId: shop.id });
      setShop(updated);
      setShops((current) => current.map((value) => value.id === updated.id ? updated : value));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  async function updateInventory(listingId, inventoryCount) {
    setError("");
    onListingUpdated({ id: listingId, inventory_count: inventoryCount });
    try {
      const updated = await updateListing({
        accessToken,
        apiUrl: API_URL,
        listingId,
        listing: { inventory_count: inventoryCount },
      });
      onListingUpdated(updated);
    } catch (requestError) {
      setError(errorMessage(requestError));
      await refreshListings();
    }
  }

  async function updateShopCategory(listingId, shopCategory) {
    setError("");
    try {
      const updated = await updateListing({
        accessToken,
        apiUrl: API_URL,
        listingId,
        listing: { shop_category: shopCategory || null },
      });
      onListingUpdated(updated);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function removeItem(listing) {
    if (!window.confirm(`Vil du slette "${listing.title}"? Varen fjernes fra butikken.`)) return;

    setDeletingListingId(listing.id);
    setError("");
    try {
      await deleteListing({ accessToken, apiUrl: API_URL, listingId: listing.id });
      onListingDeleted(listing.id);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setDeletingListingId("");
    }
  }

  async function createItem(item, images) {
    setIsCreatingItem(true);
    setError("");
    try {
      const payload = { ...listingPayload(item), shop_id: shop.id };
      if (images.length) {
        await createListingWithImages({ accessToken, apiUrl: API_URL, images, listing: payload });
      } else {
        await createListing({ accessToken, apiUrl: API_URL, listing: payload });
      }
      await refreshListings();
      return true;
    } catch (requestError) {
      setError(errorMessage(requestError));
      await refreshListings();
      return false;
    } finally {
      setIsCreatingItem(false);
    }
  }

  async function generateDescription(images) {
    if (!images.length) return null;
    setIsGeneratingDescription(true);
    setError("");
    try {
      return await analyzeListingImage({
        accessToken,
        apiUrl: API_URL,
        images: images.slice(0, 3),
      });
    } catch (requestError) {
      setError(errorMessage(requestError));
      return null;
    } finally {
      setIsGeneratingDescription(false);
    }
  }

  if (isLoading) {
    return <section className="main-panel"><div className="empty-results">Henter din butik...</div></section>;
  }

  if (view === "dashboard" && !shop) {
    return <MiniShopsPage error={error} isSaving={isSaving} onCreate={createMiniShop} onEdit={editShop} shops={shops} />;
  }

  const shopListings = listings.filter((listing) => listing.shop_id === shop?.id);

  if (view === "settings") {
    return (
      <ShopSettingsPage
        error={error}
        isSaving={isSaving}
        listings={shopListings}
        mediaUrl={mediaUrl}
        onBack={() => onNavigate("shop")}
        onPreviewShop={() => onNavigate("shop-preview")}
        onDeleteShop={removeShop}
        onSaveBranding={saveBranding}
        shop={shop}
      />
    );
  }

  if (view === "dashboard" || view === "preview") {
    return (
      <ShopPreviewPage
        error={error}
        listings={shopListings}
        mediaUrl={mediaUrl}
        onAddItem={() => onNavigate("shop-create")}
        onBackToShops={() => setShop(null)}
        onEditShop={() => onNavigate("shop-settings")}
        onManageStock={() => onNavigate("shop-stock")}
        onOpenListing={onOpenListing}
        shop={shop}
      />
    );
  }

  return (
    <ShopDashboardPage
      error={error}
      isLoading={false}
      isCreatingItem={isCreatingItem}
      isGeneratingDescription={isGeneratingDescription}
      listings={shopListings}
      deletingListingId={deletingListingId}
      mode={view === "stock" ? "stock" : "create"}
      onBack={() => onNavigate("shop")}
      onCreateItem={createItem}
      onGenerateDescription={generateDescription}
      onDeleteItem={removeItem}
      onUpdateShopCategory={updateShopCategory}
      onUpdateInventory={updateInventory}
      shopCategories={shop?.shop_categories || []}
    />
  );
}

function listingPayload(item) {
  return {
    title: item.title,
    description: item.description,
    category: item.category,
    shop_category: item.shopCategory || null,
    condition: item.condition || null,
    price_amount: Number(item.priceAmount),
    price_currency: "DKK",
    location: item.location || null,
    inventory_count: Number(item.inventoryCount),
  };
}

function errorMessage(error) {
  return error.response?.data?.detail || error.message;
}

export default ShopDashboardContainer;
